import React, { useContext, useEffect, useState } from 'react'
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiArrowUpRight, FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import { viUpdatesApi } from '../../Config/config';
import { UserContext } from '../../Contexts/UserContext';
import Loader from '../Loader';
import Constants from '../../Constants';

/* Media carousel for the "VI Updates" tab. An empty response leaves the space blank,
but a failed one shows a retry prompt: this is the default tab for eligible
subscribers, so a silently blank panel is indistinguishable from an outage. */

/* VI Updates is available to every subscriber except CFS-only ones: holding any
database besides CFS grants access, whichever one it is. */
export function hasVIUpdatesAccess() {
    const allowedModulesList = JSON.parse(localStorage.getItem(Constants.localStorageKeys.allowedModules)) ?? [];
    return allowedModulesList.some(module => module !== 'cfs');
}

function isExternalUrl(url) {
    return /^https?:\/\//i.test(url ?? '');
}

function VIUpdates() {
    const navigate = useNavigate();
    const { getToken } = useContext(UserContext);
    const hasAccess = hasVIUpdatesAccess();
    const [updates, setUpdates] = useState([]);
    /* Seeded from hasAccess so the first paint is the loader rather than a blank
    frame that reads as an empty response */
    const [isLoading, setIsLoading] = useState(hasAccess);
    const [hasError, setHasError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (!hasAccess) return;
        const controller = new AbortController();
        setIsLoading(true);
        setHasError(false);
        axios.get(viUpdatesApi, {
            headers: {
                "Authorization": getToken()
            },
            signal: controller.signal,
        }).then(response => {
            setUpdates(response?.data?.updates ?? []);
            setIsLoading(false);
        }).catch(error => {
            /* Aborted means the tab was switched away mid-flight and the component is
            unmounting, so leave state untouched */
            if (axios.isCancel(error)) return;
            console.error('Error fetching VI updates:', error);
            setUpdates([]);
            setHasError(true);
            setIsLoading(false);
        });
        return () => controller.abort();
        /* getToken is deliberately not a dependency: UserContext rebuilds it on every
        render, so depending on it would abort and restart the fetch in a loop.
        reloadKey is what drives a manual retry. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAccess, reloadKey]);

    useEffect(() => {
        setActiveIndex(0);
    }, [updates]);

    const goTo = (index) => {
        setActiveIndex(index);
    };

    const onUpdateClick = (linkUrl) => {
        if (!linkUrl) return;
        if (isExternalUrl(linkUrl)) {
            window.open(linkUrl, '_blank', 'noopener,noreferrer');
        } else {
            navigate(linkUrl);
        }
    };

    const activeUpdate = updates[activeIndex];
    const activeCaption = activeUpdate?.caption;
    const isExternalLink = isExternalUrl(activeUpdate?.link_url);
    const hasMultiple = updates.length > 1;
    /* The whole caption strip is a hit target for the same link as the media below it, so it
    renders as a real anchor for external links (middle-click / open-in-new-tab) and as a plain
    div carrying the shared click handler for in-app routes. With no link_url the handler no-ops
    and no hover affordance is applied, leaving the strip inert. */
    const CaptionStrip = isExternalLink ? 'a' : 'div';
    const captionStripProps = isExternalLink
        ? { href: activeUpdate.link_url, target: '_blank', rel: 'noopener noreferrer' }
        : { onClick: () => onUpdateClick(activeUpdate?.link_url) };
    const captionStripClasses = `group/caption relative shrink-0 h-[38px] flex items-center justify-center px-12 bg-[#FBF8F1] border-b border-[#EFE6D2]${activeUpdate?.link_url ? ' cursor-pointer hover:bg-[#F5ECD5] transition-colors' : ''}`;
    const captionClasses = `min-w-0 text-[16px] leading-[18px] font-sans_book_body font-semibold text-[#333333] text-center truncate${activeUpdate?.link_url ? ' group-hover/caption:text-[#BC8B20] transition-colors' : ''}`;

    if (!hasAccess) {
        return null;
    }

    return (
        <div className="w-full h-full min-h-[25rem] sm:min-h-[24rem] lg:min-h-[30rem] xl:min-h-0 p-2 flex flex-col">
            {isLoading ? (
                <div className="w-full flex-1 min-h-0 flex items-center justify-center">
                    <Loader />
                </div>
            ) : hasError ? (
                <div className="w-full flex-1 min-h-0 flex flex-col items-center justify-center gap-3">
                    <span className="w-11 h-11 rounded-full bg-[#FDF7E9] border border-[#EADCB4] flex items-center justify-center text-[#BC8B20]">
                        <FiAlertCircle size={20} />
                    </span>
                    <span className="text-[15px] leading-[18px] font-sans_book_body text-[#666666] text-center px-6">
                        VI Updates could not be loaded right now.
                    </span>
                    <button
                        onClick={() => setReloadKey(key => key + 1)}
                        className="flex items-center gap-1.5 text-[13px] leading-[16px] font-sans_medium_body text-[#BC8B20] border border-[#BC8B20] rounded-[10.5px] px-3 py-1 hover:bg-[#BC8B20] hover:text-white transition-colors"
                    >
                        <FiRefreshCw size={13} />
                        Retry
                    </button>
                </div>
            ) : updates.length === 0 ? (
                <div className="w-full flex-1 min-h-0" />
            ) : (
                <div className="flex flex-col flex-1 min-h-0">
                    {/* One card: caption strip as its header, media as its body */}
                    <div className="group flex flex-col flex-1 min-h-0 rounded-[8px] border border-[#E6E6E6] overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
                        {/* Header strip holds a fixed height so the media below never shifts between slides.
                        With one uncaptioned update there is nothing to show, so the strip is dropped entirely. */}
                        {(activeCaption || hasMultiple) && (
                            <CaptionStrip {...captionStripProps} className={captionStripClasses}>
                                {activeCaption && (
                                    <span className={captionClasses}>
                                        {activeCaption}
                                    </span>
                                )}
                                {/* Pinned right rather than a flex sibling, so its width never pulls the caption off centre */}
                                {hasMultiple && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] leading-[14px] font-sans_book_body font-semibold text-[#8C7433]">
                                        {activeIndex + 1} / {updates.length}
                                    </span>
                                )}
                            </CaptionStrip>
                        )}

                        {/* Media body. The side gutters are reserved for the overlaid arrows,
                        so no part of a slide sits underneath them. The wash is held at low
                        opacity so it stays a hint of tint behind the creative. */}
                        <div className="relative flex-1 min-h-0 bg-gradient-to-br from-[#FCFCFC]/60 via-[#F6F4EF]/60 to-[#EFEAE0]/60">
                            {activeUpdate && (
                                <div
                                    key={activeIndex}
                                    onClick={() => onUpdateClick(activeUpdate.link_url)}
                                    className={`vi-updates-slide absolute inset-0 flex items-center justify-center p-2 ${hasMultiple ? 'sm:px-12' : ''} ${activeUpdate.link_url ? 'cursor-pointer' : ''}`}
                                >
                                    {activeUpdate.type === 'video' ? (
                                        <video
                                            src={activeUpdate.url}
                                            controls
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full h-full object-contain rounded-[4px]"
                                        />
                                    ) : isExternalLink ? (
                                        /* Linked images render as a real anchor so middle-click and
                                        open-in-new-tab work; stopPropagation keeps the frame's own
                                        click handler from opening a second tab. Unlinked images stay
                                        a bare <img> — an <a> with an empty href would just
                                        re-navigate to the current page. */
                                        <a
                                            href={activeUpdate.link_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full h-full flex items-center justify-center"
                                        >
                                            <img
                                                src={activeUpdate.url}
                                                alt={activeUpdate.title ?? 'VI Update'}
                                                className="w-full h-full object-contain rounded-[4px] transition-transform duration-300 group-hover:scale-[1.015]"
                                            />
                                        </a>
                                    ) : (
                                        <img
                                            src={activeUpdate.url}
                                            alt={activeUpdate.title ?? 'VI Update'}
                                            className={`w-full h-full object-contain rounded-[4px] transition-transform duration-300 ${activeUpdate.link_url ? 'group-hover:scale-[1.015]' : ''}`}
                                        />
                                    )}
                                </div>
                            )}

                            {/* Click affordance for linked slides: hover-only, so it never covers the creative */}
                            {activeUpdate?.link_url && (
                                <span className="pointer-events-none absolute bottom-2 right-2 hidden sm:flex items-center gap-1 text-[11px] leading-[14px] font-sans_book_body font-semibold text-white bg-[#BC8B20] rounded-full pl-2.5 pr-2 py-1 shadow-[0_1px_4px_rgba(0,0,0,0.2)] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    {isExternalLink ? 'Open link' : 'View details'}
                                    <FiArrowUpRight size={12} />
                                </span>
                            )}

                            {hasMultiple && (
                                <>
                                    <button
                                        onClick={() => goTo((activeIndex - 1 + updates.length) % updates.length)}
                                        aria-label="Previous update"
                                        className="absolute left-1.5 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm border border-[#E0E0E0] text-[#555555] shadow-[0_1px_5px_rgba(0,0,0,0.12)] flex items-center justify-center hover:bg-[#BC8B20] hover:text-white hover:border-[#BC8B20] transition-colors"
                                    >
                                        <FiChevronLeft size={18} />
                                    </button>
                                    <button
                                        onClick={() => goTo((activeIndex + 1) % updates.length)}
                                        aria-label="Next update"
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm border border-[#E0E0E0] text-[#555555] shadow-[0_1px_5px_rgba(0,0,0,0.12)] flex items-center justify-center hover:bg-[#BC8B20] hover:text-white hover:border-[#BC8B20] transition-colors"
                                    >
                                        <FiChevronRight size={18} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Dots: plain row below the frame */}
                    {hasMultiple && (
                        <div className="shrink-0 flex items-center justify-center gap-1.5 pt-2.5">
                            {updates.map((_, index) => (
                                <span
                                    key={index}
                                    onClick={() => goTo(index)}
                                    aria-label={`Go to update ${index + 1}`}
                                    className={`h-[6px] rounded-full cursor-pointer transition-all duration-300 ${index === activeIndex ? 'w-6 bg-gradient-to-r from-[#D6B15F] to-[#BC8B20]' : 'w-[6px] bg-[#D5D5D5] hover:bg-[#BC8B20]/60'}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default VIUpdates
